import type { TFunction } from 'i18next';
import type { ServiceCatalogueItem } from '@/types';

/**
 * SVC-STEP — turn a service's raw metric columns into merchant-facing copy.
 *
 * The backend deliberately sends numbers, not sentences: this app renders
 * Arabic by default, and an interval formatted server-side ("16-20 hours")
 * cannot be translated, mirrored or re-worded without a deploy. So the one
 * place that knows the locale does the formatting, and it is this file.
 *
 * `null` means NOT MEASURED, and a missing stat is OMITTED rather than shown
 * as 0 or a dash. Both alternatives read as results: "0% delivered" is a claim
 * nobody made, and a dash invites the merchant to interpret absence. A service
 * with no numbers therefore renders exactly as it does today — name,
 * description, price — which is the state of the whole catalogue on day one.
 */
export interface ServiceMetric {
  // Stable identifier for keys and tests; never shown.
  id: 'delivery' | 'success_rate' | 'resolution';
  label: string;
  value: string;
}

/**
 * ponytail: singular/plural picked by a ternary rather than i18next plural
 * suffixes. Arabic has six plural categories and these two units would need a
 * full table each to gain nothing — the values in play are ranges and small
 * day counts. Move to `count`-based keys if a unit ever needs the dual form.
 */
const HOURS_PER_DAY = 24;

const unit = (t: TFunction, value: number, singular: string, plural: string): string =>
  String(t(value === 1 ? singular : plural, { value }));

/**
 * The delivery estimate is an interval, and a degenerate one is rendered as a
 * single number: "20 hours", never "20-20 hours".
 *
 * Tolerating max < min matters — a partial admin PATCH can set one bound below
 * a stored other one (see the note in the trusted UpdateRequest), and this is
 * the surface where that would show. It is normalised here rather than being
 * allowed to render backwards at a merchant.
 */
const deliveryWindow = (
  t: TFunction,
  rawMin?: number | null,
  rawMax?: number | null,
): string | null => {
  const bounds = [rawMin, rawMax].filter(
    (bound): bound is number => typeof bound === 'number' && Number.isFinite(bound),
  );

  if (bounds.length === 0) return null;

  /*
   * 🔴 EDD IS QUOTED IN DAYS, AND THE BACKEND STILL STORES HOURS.
   *
   * Owner ruling 2026-08-31: "1 day Select, 1-2 day Save". The column stays
   * `delivery_hours_min/max` — one source of truth, at the finest resolution
   * anyone has — and the conversion happens here, where the locale already is.
   *
   * CEIL, not round. An EDD is a PROMISE, and 30 hours is "2 days" to a
   * merchant waiting on it; rounding would let a 36-hour window advertise
   * itself as 1 day and be late by design. Ceiling is the direction that can
   * only ever under-promise.
   */
  const min = Math.max(1, Math.ceil(Math.min(...bounds) / HOURS_PER_DAY));
  const max = Math.max(1, Math.ceil(Math.max(...bounds) / HOURS_PER_DAY));

  return min === max
    ? unit(t, min, 'service_day', 'service_days')
    : String(t('service_days_range', { min, max }));
};

/**
 * The column heading for a metric, by id.
 *
 * ONE definition, shared by the per-row cells and the table header the
 * redesign added. Writing the header labels out again next to the table would
 * be the same rule in two files — and the two would drift the first time a
 * translation key changed, leaving a column headed one thing and filled with
 * another.
 */
export const metricLabel = (t: TFunction, id: ServiceMetric['id']): string => {
  switch (id) {
    // 🔴 BOXY-240 — the column is "Delivery", not "Estimated Delivery".
    //
    // Its OWN key rather than re-wording `estimated_delivery`: that key is also
    // the finance card's row title and the carrier step's column, and neither
    // asked for this. A shared key re-worded to suit one caller is how two
    // screens end up saying something a third of them meant.
    case 'delivery':
      return String(t('service_metric_delivery'));
    // ⚠️ "Next Day", and the key behind it is still `success_rate`. The
    // 2026-08-23 design heads this column NEXT DAY in both products and binds
    // it to its own successRate value; no next-day column exists anywhere in
    // the backend. The label is the design's, the number is the one we have.
    case 'success_rate':
      return String(t('service_metric_success_rate'));
    case 'resolution':
      return String(t('service_metric_resolution'));
  }
};

/**
 * Every stat the service actually has, in the order the design shows them.
 * Callers render the array — an empty one is a valid, expected result.
 */
export const buildServiceMetrics = (
  t: TFunction,
  service: ServiceCatalogueItem,
): ServiceMetric[] => {
  const metrics: ServiceMetric[] = [];

  const delivery = deliveryWindow(t, service.delivery_hours_min, service.delivery_hours_max);
  if (delivery) {
    metrics.push({ id: 'delivery', label: metricLabel(t, 'delivery'), value: delivery });
  }

  if (typeof service.success_rate === 'number' && Number.isFinite(service.success_rate)) {
    metrics.push({
      id: 'success_rate',
      label: metricLabel(t, 'success_rate'),
      // Trailing ".0" is noise on a headline figure, so 98 shows as "98%" and
      // 97.4 keeps its decimal. Not toFixed(1) for both: padding an integer
      // implies a precision the number does not have.
      value: String(
        t('service_success_rate_value', { value: Number(service.success_rate.toFixed(1)) }),
      ),
    });
  }

  // A percentage on the same scale as success_rate, but NOT its formatter —
  // this one carries the design's "same day" qualifier and that one must not.
  // Order matters: the design reads delivery -> next day -> resolution, and
  // callers render the array as given.
  if (
    typeof service.same_day_resolution_rate === 'number' &&
    Number.isFinite(service.same_day_resolution_rate)
  ) {
    metrics.push({
      id: 'resolution',
      label: metricLabel(t, 'resolution'),
      // The QUALIFIER lives in the value, not the label — the design heads
      // this column "RESOLUTION" and reads it "95% same day". In the label it
      // would sit over the narrowest track on the row with a bare percentage
      // under it.
      value: String(
        t('service_resolution_value', {
          value: Number(service.same_day_resolution_rate.toFixed(1)),
        }),
      ),
    });
  }

  return metrics;
};

export default buildServiceMetrics;

/**
 * Every metric this app can show, in the order the design reads them.
 *
 * ⭐ THREE, AND THAT IS A DESIGN DECISION RATHER THAN A DATA ONE. The API still
 * sends `sla_breach_rate` and `rto_handling_days` and `ServiceCatalogueItem`
 * still carries them; the 2026-08-23 design does not show them. Dropping the
 * type fields would be the wrong response — ops fills those columns and a later
 * design may read them again.
 *
 * `buildServiceMetrics` returns only the ones a given service HAS, which is
 * what a prose row wants. A comparison table wants the full set, so that the
 * same metric lands in the same column on every row — hence this being
 * exported rather than implied by the order of the pushes above.
 */
export const SERVICE_METRIC_ORDER: ServiceMetric['id'][] = [
  'delivery',
  'success_rate',
  'resolution',
];
